import React, {useEffect, useState} from 'react';
import '../index.css';
import UsersTable from "../components/users/UsersTable";
import GroupsManager from "../components/workflows/GroupsManager";
import SubstitutionsManager from "../components/workflows/SubstitutionsManager";
import DelegationsManager from "../components/workflows/DelegationsManager";
import UpdateAuthCount from "../components/users/UpdateAuthCount";
import { useAuth } from '../context/authContext';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { getUsers, deleteUser, createUser, updateUser } from '../services/user.service';
import { useToast } from "../components/ui/use-toast"
import { getGroups } from '../services/group.service';
import { getOrganisation } from '../services/organisation.service';
import { getSubstitutions } from '../services/substitution.service';
import { getDelegations } from '../services/delegation.service';
import { TableCardSkeleton } from '../components/ui/card-skeletons';
import AsyncContent from '../components/ui/AsyncContent';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';

const TAB_USERS = 'users-groups';
const TAB_DELEGATION = 'approval-delegation';

const UsersPage = () => {

    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [substitutions, setSubstitutions] = useState([]);
    const [delegations, setDelegations] = useState([]);
    const [isCustom, setIsCustom] = useState(false);
    const [authCount, setAuthCount] = useState(1);
    const [restrictSubmitterAuth, setRestrictSubmitterAuth] = useState(false);
    const [activeTab, setActiveTab] = useState(TAB_USERS);
    const [loading, setLoading] = useState(true);
    const [reload, setReload] = useState(false)
    const { user } = useAuth();
    const { advancedFeaturesEnabled } = useFeatureAccess();
    const { toast } = useToast()

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const requests = [
                getUsers(user.token, user.organisationId),
                getOrganisation(user.organisationId, user.token),
            ];
            if (advancedFeaturesEnabled) {
                requests.push(
                    getGroups(user.token, user.organisationId),
                    getSubstitutions(user.token, user.organisationId),
                    getDelegations(user.token, user.organisationId),
                );
            }

            const [usersRes, orgRes, groupsRes, substitutionsRes, delegationsRes] =
                await Promise.all(requests);

            if (usersRes && usersRes.status === 200) {
                const data = usersRes.data?.data;
                setUsers(Array.isArray(data) ? data : []);
            }
            if (orgRes && orgRes.status === 200) {
                const org = orgRes.data?.data;
                if (org) {
                    setIsCustom(org.approvalProcessType === 'CUSTOM');
                    setAuthCount(org.requiredAuthorisations);
                    setRestrictSubmitterAuth(org.restrictSubmitterAuth);
                }
            }
            if (groupsRes && groupsRes.status === 200) {
                const data = groupsRes.data?.data;
                setGroups(Array.isArray(data) ? data : []);
            }
            if (substitutionsRes && substitutionsRes.status === 200) {
                const data = substitutionsRes.data?.data;
                setSubstitutions(Array.isArray(data) ? data : []);
            }
            if (delegationsRes && delegationsRes.status === 200) {
                const data = delegationsRes.data?.data;
                setDelegations(Array.isArray(data) ? data : []);
            }
            setLoading(false);
        };

        fetchData();
    }, [user.token, user.organisationId, reload, advancedFeaturesEnabled]);

    const triggerReload = () => setReload((prev) => !prev);

    const handleCreateUser = async (newUser) => {
        if (newUser.maxAuthValue > 2147483647) {
            toast({
                title: "Error",
                description: "Max Auth Value is too large",
                variant: "destructive",
            });
            return false;
        }

        const response = await createUser(user.token, user.organisationId, newUser);
        if (response && response.status === 201) {
            toast({
                title: "Success",
                description: "User created successfully",
                variant: "success",
            });
            triggerReload();
            return true;
        }

        toast({
            title: "Error",
            description: "There was an error creating the user",
            variant: "destructive",
        });
        return false;
    }

    const handleDeleteUser = async (userId) => {
        if (users.length === 1 && userId === user.id) {
            toast({
                title: "Error",
                description: "There must be at least one admin user in the organisation",
                variant: "destructive",
            });
            return false;
        }

        const response = await deleteUser(user.token, user.organisationId, userId);
        if (response && response.status === 200) {
            setUsers(users.filter(user => user.id !== userId));
            triggerReload();
            toast({
                title: "Success",
                description: "User deleted successfully",
                variant: "success",
            })
            return true;
        }

        toast({
            title: "Error",
            description: "There was an error deleting the user",
            variant: "destructive",
        })
        return false;
    }

    const handleUpdateUser = async (selectedUser) => {
        const response = await updateUser(user.token, user.organisationId, selectedUser.id, selectedUser);
        if (response && response.status === 200) {
            triggerReload();
            toast({
                title: "Success",
                description: "User updated successfully",
                variant: "success",
            });
        } else {
            toast({
                title: "Error",
                description: "There was an error updating the user",
                variant: "destructive",
            });
        }
    }

    const usersTable = (
        <AsyncContent
            loading={loading}
            skeleton={<TableCardSkeleton titleWidth="w-16" rows={6} cols={7} />}
        >
            <UsersTable
                users={users}
                setUsers={setUsers}
                onDeleteUser={handleDeleteUser}
                onUpdateUser={handleUpdateUser}
                onCreateUser={handleCreateUser}
            />
        </AsyncContent>
    );

    if (!advancedFeaturesEnabled) {
        return (
            <div className="App">
                <div className="max-w-7xl mx-auto flex flex-col gap-8">
                    {usersTable}
                    <AsyncContent
                        loading={loading}
                        skeleton={<TableCardSkeleton titleWidth="w-44" rows={3} cols={2} withAction={false} />}
                    >
                        <UpdateAuthCount
                            users={users}
                            authCount={authCount}
                            setAuthCount={setAuthCount}
                            restrictSubmitterAuth={restrictSubmitterAuth}
                            setRestrictSubmitterAuth={setRestrictSubmitterAuth}
                        />
                    </AsyncContent>
                </div>
            </div>
        );
    }

    return (
        <div className="App">
            <div className="max-w-7xl mx-auto flex flex-col gap-8">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value={TAB_USERS}>Users & Groups</TabsTrigger>
                        <TabsTrigger value={TAB_DELEGATION}>Approval Routing</TabsTrigger>
                    </TabsList>

                    <TabsContent value={TAB_USERS}>
                        {usersTable}
                        <AsyncContent
                            loading={loading}
                            skeleton={<TableCardSkeleton titleWidth="w-36" rows={3} cols={4} />}
                        >
                            <GroupsManager groups={groups} users={users} onChanged={triggerReload} />
                        </AsyncContent>
                    </TabsContent>

                    <TabsContent value={TAB_DELEGATION}>
                        {!loading && !isCustom ? (
                            <Card>
                                <CardContent className="pt-6">
                                    <p className="text-sm text-gray-500 mb-0">
                                        Approval delegation is available when your organisation uses custom approval
                                        workflows. Switch to custom workflows on the Workflows page to manage
                                        substitutions and delegations.
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            <>  
                                <AsyncContent
                                    loading={loading}
                                    skeleton={<TableCardSkeleton titleWidth="w-36" rows={3} cols={5} />}
                                >
                                    <DelegationsManager
                                        delegations={delegations}
                                        users={users}
                                        onChanged={triggerReload}
                                    />
                                </AsyncContent>
                                <AsyncContent
                                    loading={loading}
                                    skeleton={<TableCardSkeleton titleWidth="w-36" rows={3} cols={4} />}
                                >
                                    <SubstitutionsManager
                                        substitutions={substitutions}
                                        users={users}
                                        onChanged={triggerReload}
                                    />
                                </AsyncContent>
                            </>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
};

export default UsersPage;
